import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, Save, Plus, Trash2, RotateCcw } from "lucide-react";
import type React from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { checkPainelAuth } from "@/lib/painel-auth.functions";
import { getMatrixById, updateMatrix } from "@/lib/matrices.functions";
import {
  DEFAULT_CONTENT,
  mergeMatrixContent,
  type MatrixContent,
  type BenefitItem,
  type ProcessStep,
  type FaqItem,
} from "@/lib/matrix-content";

export const Route = createFileRoute("/painel/matriz/$id/editar")({
  head: () => ({
    meta: [
      { title: "Editor visual de matriz" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: MatrixEditorPage,
});

type MatrixMeta = { id: string; nome: string; palavra_chave_base: string; segmento: string };

function MatrixEditorPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();

  const authQuery = useQuery({
    queryKey: ["painel-auth"],
    queryFn: async () => checkPainelAuth(),
  });

  useEffect(() => {
    if (authQuery.isSuccess && !authQuery.data) {
      navigate({ to: "/painel" });
    }
  }, [authQuery.isSuccess, authQuery.data, navigate]);

  const matrixQuery = useQuery({
    queryKey: ["matrix", id],
    queryFn: async () => getMatrixById({ data: { id } }),
    enabled: !!authQuery.data,
  });

  if (!authQuery.data || matrixQuery.isLoading) {
    return (
      <div className="grid min-h-screen place-items-center text-sm text-muted-foreground">
        Carregando…
      </div>
    );
  }
  if (!matrixQuery.data) {
    return (
      <div className="grid min-h-screen place-items-center text-sm text-muted-foreground">
        Matriz não encontrada.
      </div>
    );
  }

  return <Editor matrix={matrixQuery.data} />;
}

function Editor({ matrix }: { matrix: any }) {
  const meta: MatrixMeta = {
    id: matrix.id,
    nome: matrix.nome,
    palavra_chave_base: matrix.palavra_chave_base,
    segmento: matrix.segmento,
  };
  const [content, setContent] = useState<MatrixContent>(() => mergeMatrixContent(matrix));
  const [dirty, setDirty] = useState(false);

  const update = <K extends keyof MatrixContent>(k: K, v: MatrixContent[K]) => {
    setContent((c) => ({ ...c, [k]: v }));
    setDirty(true);
  };

  const saveMutation = useMutation({
    mutationFn: async () =>
      updateMatrix({
        data: {
          id: meta.id,
          nome: meta.nome,
          palavra_chave_base: meta.palavra_chave_base,
          segmento: meta.segmento,
          ...content,
        } as any,
      }),
    onSuccess: () => {
      toast.success("Matriz salva");
      setDirty(false);
    },
    onError: (e: any) => toast.error(e?.message ?? "Erro ao salvar"),
  });

  return (
    <div className="min-h-screen bg-muted/30 pb-32">
      {/* Topbar */}
      <div className="sticky top-0 z-40 border-b bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3">
          <div className="flex items-center gap-3">
            <Link
              to="/painel"
              className="inline-flex h-9 items-center gap-1.5 rounded-md border px-3 text-sm text-brand-navy hover:bg-muted"
            >
              <ArrowLeft className="h-4 w-4" /> Voltar
            </Link>
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">
                Editor visual · Matriz
              </div>
              <div className="font-display text-base font-bold text-brand-navy">
                {meta.nome}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {dirty && (
              <span className="text-xs font-medium text-brand-orange">
                Alterações não salvas
              </span>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (confirm("Restaurar todos os textos originais?")) {
                  setContent(DEFAULT_CONTENT);
                  setDirty(true);
                }
              }}
            >
              <RotateCcw className="mr-1.5 h-4 w-4" /> Restaurar padrão
            </Button>
            <Button
              size="sm"
              disabled={!dirty || saveMutation.isPending}
              onClick={() => saveMutation.mutate()}
              className="bg-brand-orange text-white hover:bg-brand-orange/90"
            >
              <Save className="mr-1.5 h-4 w-4" />
              {saveMutation.isPending ? "Salvando…" : "Salvar"}
            </Button>
          </div>
        </div>
        <div className="mx-auto max-w-6xl px-4 pb-3 text-[11px] text-muted-foreground">
          Clique em qualquer texto para editar. Use{" "}
          <code className="rounded bg-muted px-1">**palavra**</code> para
          destacar em laranja. Tokens:{" "}
          <code className="rounded bg-muted px-1">{"{cidade}"}</code>{" "}
          <code className="rounded bg-muted px-1">{"{bairro}"}</code>{" "}
          <code className="rounded bg-muted px-1">{"{no_cidade}"}</code>{" "}
          <code className="rounded bg-muted px-1">{"{no_bairro}"}</code>{" "}
          <code className="rounded bg-muted px-1">{"{no_bairro_ou_cidade}"}</code>{" "}
          <code className="rounded bg-muted px-1">{"{palavra_chave}"}</code>{" "}
          <code className="rounded bg-muted px-1">{"{palavra_chave_lower}"}</code>.
        </div>
      </div>

      <div className="mx-auto max-w-4xl space-y-8 px-4 py-8">
        <SectionCard label="Hero">
          <Field label="Eyebrow" value={content.hero_eyebrow} onChange={(v) => update("hero_eyebrow", v)} />
          <Field label="Título (aceita **destaque**)" multiline value={content.hero_title} onChange={(v) => update("hero_title", v)} />
          <Field label="Subtítulo" multiline value={content.hero_subtitle} onChange={(v) => update("hero_subtitle", v)} />
          <Field label="Botão CTA" value={content.hero_button} onChange={(v) => update("hero_button", v)} />
        </SectionCard>

        <SectionCard label="Profissões (marquee)">
          <ListStringEditor
            items={content.profissoes}
            onChange={(v) => update("profissoes", v)}
            placeholder="Ex: Pedreiros"
          />
        </SectionCard>

        <SectionCard label="Benefícios">
          <Field label="Eyebrow" value={content.benefits_eyebrow} onChange={(v) => update("benefits_eyebrow", v)} />
          <Field label="Título" multiline value={content.benefits_title} onChange={(v) => update("benefits_title", v)} />
          <Field label="Texto de apoio" multiline value={content.benefits_intro} onChange={(v) => update("benefits_intro", v)} />
          <ListPairEditor
            keyA="title"
            keyB="desc"
            labelA="Título"
            labelB="Descrição"
            items={content.benefits}
            onChange={(v) => update("benefits", v as BenefitItem[])}
          />
        </SectionCard>

        <SectionCard label="Processo">
          <Field label="Eyebrow" value={content.process_eyebrow} onChange={(v) => update("process_eyebrow", v)} />
          <Field label="Título" multiline value={content.process_title} onChange={(v) => update("process_title", v)} />
          <ListPairEditor
            keyA="title"
            keyB="desc"
            labelA="Etapa"
            labelB="Descrição"
            items={content.process_steps}
            onChange={(v) => update("process_steps", v as ProcessStep[])}
          />
          <Field label="Botão do meio (CTA)" value={content.cta_mid_button} onChange={(v) => update("cta_mid_button", v)} />
        </SectionCard>

        <SectionCard label="Sobre">
          <Field label="Eyebrow" value={content.about_eyebrow} onChange={(v) => update("about_eyebrow", v)} />
          <Field label="Título" multiline value={content.about_title} onChange={(v) => update("about_title", v)} />
          <Field label="Parágrafo 1" multiline value={content.about_p1} onChange={(v) => update("about_p1", v)} />
          <Field label="Parágrafo 2" multiline value={content.about_p2} onChange={(v) => update("about_p2", v)} />
        </SectionCard>

        <SectionCard label="FAQ">
          <Field label="Eyebrow" value={content.faq_eyebrow} onChange={(v) => update("faq_eyebrow", v)} />
          <Field label="Título" value={content.faq_title} onChange={(v) => update("faq_title", v)} />
          <Field label="Texto de apoio" multiline value={content.faq_intro} onChange={(v) => update("faq_intro", v)} />
          <ListPairEditor
            keyA="q"
            keyB="a"
            labelA="Pergunta"
            labelB="Resposta"
            items={content.faq}
            onChange={(v) => update("faq", v as FaqItem[])}
            multilineB
          />
        </SectionCard>

        <SectionCard label="Conteúdo SEO (texto longo)">
          <Field label="Título" multiline value={content.seo_title} onChange={(v) => update("seo_title", v)} />
          <Field label="Parágrafo 1" multiline value={content.seo_p1} onChange={(v) => update("seo_p1", v)} />
          <Field label="Parágrafo 2" multiline value={content.seo_p2} onChange={(v) => update("seo_p2", v)} />
          <Field label="Parágrafo 3" multiline value={content.seo_p3} onChange={(v) => update("seo_p3", v)} />
        </SectionCard>

        <SectionCard label="CTA final">
          <Field label="Eyebrow" value={content.cta_final_eyebrow} onChange={(v) => update("cta_final_eyebrow", v)} />
          <Field label="Título" multiline value={content.cta_final_title} onChange={(v) => update("cta_final_title", v)} />
          <Field label="Subtítulo" multiline value={content.cta_final_subtitle} onChange={(v) => update("cta_final_subtitle", v)} />
          <Field label="Botão" value={content.cta_final_button} onChange={(v) => update("cta_final_button", v)} />
        </SectionCard>
      </div>

      {/* Save bar fixo */}
      <div className="fixed bottom-4 left-1/2 z-40 -translate-x-1/2">
        <Button
          size="lg"
          disabled={!dirty || saveMutation.isPending}
          onClick={() => saveMutation.mutate()}
          className="bg-brand-orange text-white shadow-lg hover:bg-brand-orange/90"
        >
          <Save className="mr-2 h-4 w-4" />
          {saveMutation.isPending
            ? "Salvando…"
            : dirty
              ? "Salvar alterações"
              : "Tudo salvo"}
        </Button>
      </div>
    </div>
  );
}

function SectionCard({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border bg-white p-6 shadow-sm">
      <div className="mb-4 text-xs font-semibold uppercase tracking-[0.14em] text-brand-orange">
        {label}
      </div>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function Field({
  label,
  value,
  onChange,
  multiline,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  multiline?: boolean;
}) {
  return (
    <label className="block">
      <div className="mb-1.5 text-xs font-medium text-muted-foreground">{label}</div>
      {multiline ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={Math.min(6, Math.max(2, Math.ceil(value.length / 80)))}
          className="w-full resize-y rounded-md border bg-white px-3 py-2 font-display text-[15px] leading-relaxed text-brand-navy shadow-sm focus:border-brand-orange focus:outline-none focus:ring-1 focus:ring-brand-orange"
        />
      ) : (
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="text-brand-navy"
        />
      )}
    </label>
  );
}

function ListStringEditor({
  items,
  onChange,
  placeholder,
}: {
  items: string[];
  onChange: (v: string[]) => void;
  placeholder?: string;
}) {
  return (
    <div className="space-y-2">
      {items.map((it, i) => (
        <div key={i} className="flex items-center gap-2">
          <Input
            value={it}
            placeholder={placeholder}
            onChange={(e) => {
              const next = [...items];
              next[i] = e.target.value;
              onChange(next);
            }}
          />
          <button
            type="button"
            onClick={() => onChange(items.filter((_, j) => j !== i))}
            className="inline-flex h-9 w-9 items-center justify-center rounded-md border text-muted-foreground hover:text-red-600"
            title="Remover"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => onChange([...items, ""])}
        className="inline-flex items-center gap-1.5 rounded-md border border-dashed px-3 py-2 text-xs font-medium text-muted-foreground hover:border-brand-orange hover:text-brand-orange"
      >
        <Plus className="h-3.5 w-3.5" /> Adicionar item
      </button>
    </div>
  );
}

function ListPairEditor({
  items,
  onChange,
  keyA,
  keyB,
  labelA,
  labelB,
  multilineB,
}: {
  items: Array<Record<string, string>>;
  onChange: (v: Array<Record<string, string>>) => void;
  keyA: string;
  keyB: string;
  labelA: string;
  labelB: string;
  multilineB?: boolean;
}) {
  const move = (from: number, to: number) => {
    if (to < 0 || to >= items.length) return;
    const next = [...items];
    const [it] = next.splice(from, 1);
    next.splice(to, 0, it);
    onChange(next);
  };
  return (
    <div className="space-y-3">
      {items.map((it, i) => (
        <div key={i} className="rounded-md border bg-muted/30 p-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-[11px] font-semibold uppercase text-muted-foreground">
              #{i + 1}
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => move(i, i - 1)}
                disabled={i === 0}
                className="rounded border px-1.5 py-0.5 text-xs text-muted-foreground hover:text-brand-navy disabled:opacity-30"
              >
                ↑
              </button>
              <button
                type="button"
                onClick={() => move(i, i + 1)}
                disabled={i === items.length - 1}
                className="rounded border px-1.5 py-0.5 text-xs text-muted-foreground hover:text-brand-navy disabled:opacity-30"
              >
                ↓
              </button>
              <button
                type="button"
                onClick={() => onChange(items.filter((_, j) => j !== i))}
                className="rounded border px-1.5 py-0.5 text-muted-foreground hover:text-red-600"
                title="Remover"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
          <div className="space-y-2">
            <div>
              <div className="mb-1 text-[11px] text-muted-foreground">{labelA}</div>
              <Input
                value={it[keyA] ?? ""}
                onChange={(e) => {
                  const next = [...items];
                  next[i] = { ...next[i], [keyA]: e.target.value };
                  onChange(next);
                }}
              />
            </div>
            <div>
              <div className="mb-1 text-[11px] text-muted-foreground">{labelB}</div>
              {multilineB ? (
                <textarea
                  value={it[keyB] ?? ""}
                  rows={3}
                  onChange={(e) => {
                    const next = [...items];
                    next[i] = { ...next[i], [keyB]: e.target.value };
                    onChange(next);
                  }}
                  className="w-full resize-y rounded-md border bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-orange focus:outline-none focus:ring-1 focus:ring-brand-orange"
                />
              ) : (
                <Input
                  value={it[keyB] ?? ""}
                  onChange={(e) => {
                    const next = [...items];
                    next[i] = { ...next[i], [keyB]: e.target.value };
                    onChange(next);
                  }}
                />
              )}
            </div>
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={() => onChange([...items, { [keyA]: "", [keyB]: "" }])}
        className="inline-flex items-center gap-1.5 rounded-md border border-dashed px-3 py-2 text-xs font-medium text-muted-foreground hover:border-brand-orange hover:text-brand-orange"
      >
        <Plus className="h-3.5 w-3.5" /> Adicionar item
      </button>
    </div>
  );
}